// De rekenwijze achter één maatregel, stap voor stap uit het document van de
// meetstandaard — voor de doorklik 'hoe komt dit getal tot stand' op de
// detailpagina van de tegels 'Bespaarde CO2' en 'Bespaard op energierekening'.
//
// NIETS EIGENS. De formule is de tekst uit `berekening`, de factoren komen uit
// `aannames` met hun eigen bronvermelding, het kengetal en zijn onderbouwing uit
// de interventie zelf. Deze helper vult alleen de getallen in.
//
// NAGEREKEND, NIET OVERGENOMEN. De stappen worden opnieuw uitgerekend en naast
// de gepubliceerde uitkomst (`berekend`) gelegd. Wijken die af, dan zegt
// `matchesPublished` dat — de tegel rekent met de gepubliceerde waarde, en een
// lezer die de stappen naloopt moet weten dat die niet sluiten.
//
// WERPT NOOIT: geen document of onbekende code geeft null.
//
// AI-CONTEXT: See docs/features/materiaal-uitkomsten-tegels.md

/**
 * @typedef {object} CalculationStep
 * @property {string} label - Wat er bespaard wordt ('Elektriciteit', 'Aardgas', 'Drinkwater')
 * @property {number} amount - Kengetal per eenheid per jaar
 * @property {string} amountUnit - 'kWh', 'm3 gas', 'm3 water'
 * @property {string} factorName - Naam van de aanname zoals de formule hem noemt
 * @property {number} factor
 * @property {string} factorUnit
 * @property {string} bron - Bronvermelding van de aanname, letterlijk
 * @property {number} result - amount x factor
 *
 * @typedef {object} CalculationPart
 * @property {string} formula - Letterlijk uit `berekening`
 * @property {Array<CalculationStep>} steps
 * @property {number} bestendiging - Factor voor gedragsmaatregelen (1 = blijvend)
 * @property {number|null} perUnit - Nagerekend, per eenheid per jaar
 * @property {number|null} published - Zoals de standaard het publiceert
 *
 * @typedef {object} InterventionCalculation
 * @property {string} code
 * @property {string} name
 * @property {string} unit - 'per lamp', 'strekkende meter', …
 * @property {CalculationPart} co2
 * @property {CalculationPart|null} eur - null als de maatregel geen rekening verlaagt
 * @property {boolean} matchesPublished
 * @property {string} onderbouwing - Bron van het kengetal
 * @property {string} bewijssterkte
 * @property {string} statusKengetal
 * @property {string} rekenmodel
 * @property {string} afbakening
 * @property {string} standaard
 * @property {string} version
 * @property {string} releasedAt
 * @property {string} source
 */

// Welke aanname de formule bij elk kengetal gebruikt. De namen zijn het begin
// van een `id` uit `aannames` — zo schrijft de standaard ze in `berekening`.
const DRAGERS = [
    { key: 'elektraKwhPerJaar', label: 'Elektriciteit', unit: 'kWh', co2: 'emissiefactor-elektriciteit', eur: 'elektriciteitsprijs-gewogen' },
    { key: 'gasM3PerJaar', label: 'Aardgas', unit: 'm3 gas', co2: 'emissiefactor-aardgas', eur: 'gasprijs' },
    { key: 'waterM3PerJaar', label: 'Drinkwater', unit: 'm3 water', co2: 'emissiefactor-drinkwater', eur: 'waterprijs' },
]

const MARGE = 0.01

/**
 * @param {Array<object>} aannames
 * @param {string} name
 * @returns {object|null}
 */
const findAanname = (aannames, name) => aannames.find((a) => typeof a?.id === 'string' && a.id.startsWith(name)) || null

/**
 * @param {*} value
 * @returns {number}
 */
const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

/**
 * @param {object} kengetallen
 * @param {Array<object>} aannames
 * @param {'co2'|'eur'} kant
 * @returns {Array<CalculationStep>}
 */
const stepsFor = (kengetallen, aannames, kant) => DRAGERS
    .filter((drager) => number(kengetallen?.[drager.key]) !== 0)
    .map((drager) => {
        const aanname = findAanname(aannames, drager[kant])
        const amount = number(kengetallen[drager.key])
        const factor = number(aanname?.waarde)
        return {
            label: drager.label,
            amount,
            amountUnit: drager.unit,
            factorName: drager[kant],
            factor,
            factorUnit: aanname?.eenheid || '',
            bron: aanname?.bron || '',
            result: amount * factor,
        }
    })

/**
 * @param {Array<CalculationStep>} steps
 * @param {number} bestendiging
 * @returns {number|null}
 */
const total = (steps, bestendiging) => (steps.length ? steps.reduce((t, s) => t + s.result, 0) * bestendiging : null)

/**
 * @param {object|null} document - Ruw document van de interventiebibliotheek
 * @param {string} code - Interventiecode (ET02, …)
 * @returns {InterventionCalculation|null}
 */
const buildInterventionCalculation = (document, code) => {
    const raw = (document?.interventies || []).find((i) => i?.id === code)
    if (!raw) return null

    const aannames = Array.isArray(document.aannames) ? document.aannames : []
    const berekening = document.berekening || {}
    const kengetallen = raw.kengetallen || {}
    const berekend = raw.berekend || {}
    const bestendiging = Number.isFinite(Number(kengetallen.bestendiging)) && Number(kengetallen.bestendiging) > 0
        ? Number(kengetallen.bestendiging)
        : 1

    // Een maatregel met een vast CO2-getal per eenheid (hergebruik, reparatie)
    // heeft geen energiedragers om door te rekenen en verlaagt geen rekening.
    const direct = typeof kengetallen.co2ePerEenheid === 'number' ? kengetallen.co2ePerEenheid : null

    const co2Steps = direct === null ? stepsFor(kengetallen, aannames, 'co2') : []
    const eurSteps = direct === null ? stepsFor(kengetallen, aannames, 'eur') : []

    const co2 = {
        formula: direct === null ? (berekening.co2eKgPerJaar || '') : 'co2ePerEenheid',
        steps: co2Steps,
        bestendiging,
        perUnit: direct === null ? total(co2Steps, bestendiging) : direct,
        published: direct === null ? (typeof berekend.co2eKgPerJaar === 'number' ? berekend.co2eKgPerJaar : null) : direct,
    }

    const eurPublished = typeof berekend.besparingHuishoudenEurPerJaar === 'number' ? berekend.besparingHuishoudenEurPerJaar : null
    const eur = direct === null && (eurSteps.length || eurPublished !== null)
        ? {
            formula: berekening.besparingHuishoudenEurPerJaar || '',
            steps: eurSteps,
            bestendiging,
            perUnit: total(eurSteps, bestendiging),
            published: eurPublished,
        }
        : null

    const sluit = (part) => !part
        || part.published === null
        || (part.perUnit !== null && Math.abs(part.perUnit - part.published) < MARGE)

    const meta = document.meta || {}

    return {
        code,
        name: raw.interventie || code,
        unit: raw.eenheid || '',
        co2,
        eur,
        matchesPublished: sluit(co2) && sluit(eur),
        onderbouwing: raw.onderbouwing || '',
        bewijssterkte: raw.bewijssterkte || '',
        statusKengetal: raw.statusKengetal || '',
        rekenmodel: raw.rekenmodel || '',
        afbakening: raw.afbakening || '',
        standaard: meta.standaard || '',
        version: meta.version || '',
        releasedAt: meta.releasedAt || '',
        source: meta.source || '',
    }
}

export default buildInterventionCalculation
